import { EventType } from "@/analytics/analytics-events";
import { UploadImageProperties } from "@/analytics/generic-events-properties";
import { useElementFileUploadContext } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { ImageType } from "@/components/ui/tree/tree-context-menu/actions/upload-action";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { useCurrentAreaIfAvailable } from "@/modes/mode-data-context";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, GUID } from "@faro-lotv/foundation";
import { useCallback, useMemo, type JSX } from "react";
import { NewWaypointIcon } from "./new-waypoint-icon";
import { NewWaypointLoadingIndicator } from "./new-waypoint-loading-indicator";
import { WaypointNameEditor } from "./new-waypoint-name-editor";
import { useNewWaypointsStore } from "./new-waypoints-store";
import { useCurrentlyUploadingWaypoints } from "./use-currently-updating-waypoints";

/**
 * @returns UI components that visualize new waypoints in the process of being uploaded.
 */
export function NewWaypointsUI(): JSX.Element {
  const {
    newWaypoints,
    waypointForNameEditingId,
    applyNameEditToWaypoint,
    removeNewWaypoint,
    cancelNameEdit,
  } = useNewWaypointsStore();

  const currentArea = useCurrentAreaIfAvailable();

  const viewOverlay = useViewOverlayRef();

  const currentlyUploadingWaypoints = useCurrentlyUploadingWaypoints(
    currentArea?.area?.id,
  );

  const processingWaypoints = useMemo(
    () => [
      ...newWaypoints.filter(
        (waypoint) =>
          waypoint.areaId === currentArea?.area?.id &&
          waypoint.id !== waypointForNameEditingId,
      ),
      ...currentlyUploadingWaypoints,
    ],
    [
      currentArea?.area?.id,
      currentlyUploadingWaypoints,
      newWaypoints,
      waypointForNameEditingId,
    ],
  );

  const waypointForNameEditing = useMemo(
    () =>
      newWaypoints.find((waypoint) => waypoint.id === waypointForNameEditingId),
    [newWaypoints, waypointForNameEditingId],
  );

  const { uploadWaypointWithImageFiles } = useElementFileUploadContext();

  const onApplyName = useCallback(
    async (id: GUID, newName: string) => {
      const waypointToApply = newWaypoints.find(
        (waypoint) => waypoint.id === id,
      );

      assert(
        waypointToApply,
        "Expected waypoint to apply changes to be present in the store",
      );

      applyNameEditToWaypoint(id, newName);

      Analytics.track<UploadImageProperties>(
        waypointToApply.type === ImageType.Img360
          ? EventType.upload360Photo
          : EventType.upload2DPhoto,
        {
          numberOfImages: waypointToApply.files.length,
          isNewWaypoint: true,
        },
      );

      try {
        await uploadWaypointWithImageFiles(
          waypointToApply.files,
          {
            name: newName,
            id: waypointToApply.id,
            areaId: waypointToApply.areaId,
            worldPosition: waypointToApply.worldPosition,
          },
          waypointToApply.type,
        );
      } finally {
        removeNewWaypoint(id);
      }
    },
    [
      applyNameEditToWaypoint,
      newWaypoints,
      removeNewWaypoint,
      uploadWaypointWithImageFiles,
    ],
  );

  return (
    <>
      {newWaypoints.map((newWaypoint) => (
        <NewWaypointIcon
          key={newWaypoint.id}
          newWaypoint={newWaypoint}
          parentRef={viewOverlay}
        />
      ))}

      {waypointForNameEditing && (
        <WaypointNameEditor
          waypointInfo={waypointForNameEditing.id}
          parentRef={viewOverlay}
          anchorPoint={waypointForNameEditing.worldPosition}
          initialName={waypointForNameEditing.name}
          onApplyName={onApplyName}
          onExit={cancelNameEdit}
        />
      )}

      {processingWaypoints.map((waypoint) => (
        <NewWaypointLoadingIndicator
          key={waypoint.id}
          parentRef={viewOverlay}
          waypoint={waypoint}
        />
      ))}
    </>
  );
}
